import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDropRef } from '@/hook';
import {
  VButton,
  VFileBrowser,
  VFileBrowserEmptyContentList,
  VFileBrowserEmptyTree,
  VFlex,
  useVModal,
} from '@/package';
import { useCollectionManagementStore } from '@/store';
import type { S3ListItem } from '@/type';

import { LoadingIndicator } from '../LoadingIndicator';

type PathSelectorProps = {
  onSelect: (param: { basePath: string; orgPath: string }) => void;
};

const PathSelector = ({ onSelect }: PathSelectorProps) => {
  const {
    currentDirectoryUuid,
    currentBucket,
    itemByUuid,
    itemList,
    storageAccessibleBucketList,

    deselectAll,
    moveRoot,
    readDirectory: _readDirectory,
    removeDirectory,
    removeFile,
    select,
    setCurrentBucket,
    setCurrentDirectoryUuid,
    uploadDirectory,
    uploadFileList,
  } = useCollectionManagementStore();
  const { openDangerConfirm } = useVModal();
  const { t } = useTranslation();
  const [path, setPath] = useState({ basePath: '', orgPath: '' });
  const [isContentLoading, setIsContentLoading] = useState(false);
  const $el = useRef<HTMLDivElement>(null);
  const $fileBrowserContentList = useRef<HTMLDivElement>(null);

  const readDirectory = (
    param: Parameters<typeof _readDirectory>[0],
    enabledLoading = false,
  ) => {
    _readDirectory(param).finally(() => {
      setIsContentLoading(
        useCollectionManagementStore.getState().getCurrentDir().isLoading ??
          false,
      );
    });

    if (enabledLoading)
      setIsContentLoading(
        useCollectionManagementStore.getState().getCurrentDir().isLoading ??
          true,
      );
  };

  const onRemoveItemList = (itemList: S3ListItem[]) => {
    openDangerConfirm({
      title: t('storage.removeFileConfirmModalTitle.emendation'),
      message: t('storage.removeFileConfirmModalMessage.crease'),
      confirmText: t(
        'storage.removeFileConfirmModalConfirmButtonText.gemmiferousness',
      ),
      cancelText: t(
        'storage.removeFileConfirmModalCancelButtonText.ordinariness',
      ),
      onConfirm() {
        Promise.all(
          itemList.map(item => {
            if (item.isDir) {
              return removeDirectory({
                key: item.key,
              });
            } else {
              return removeFile({
                key: item.key,
              });
            }
          }),
        ).then(() => {
          setPath({ basePath: '', orgPath: '' });
        });
      },
    });
  };

  const onUploadButtonClick = (isDir: boolean = false) => {
    function openFileDialog(callback: (fileList: Nullish<FileList>) => void) {
      const input = document.createElement('input');
      input.type = 'file';
      input.multiple = true;
      input.webkitdirectory = isDir;
      input.onchange = () => callback(input.files);
      input.click();
    }

    openFileDialog(fileList => {
      if (fileList == null) return;

      uploadFileList(fileList);
    });
  };

  useEffect(() => {
    deselectAll();
    moveRoot();
    readDirectory({ childrenOption: { autoAdd: true } }, true);

    $fileBrowserContentList.current =
      $el.current!.querySelector<HTMLDivElement>(
        '[data-testid="v-virtual-scroll-container"]',
      )!;

    return () => {
      deselectAll();
    };
  }, []);

  useDropRef(
    $fileBrowserContentList,
    async event => {
      const itemList = event.dataTransfer?.items;

      if (!itemList) return;

      try {
        await uploadDirectory(itemList);
      } catch (error) {
        console.warn(error);
      } finally {
        readDirectory({
          childrenOption: {
            autoAdd: true,
          },
        });
      }
    },
    [currentDirectoryUuid, itemByUuid],
  );

  return (
    <VFlex
      ref={$el}
      className="h-[600px] w-[1020px] bg-white p-4"
      direction="col"
    >
      <VFlex className="flex-1 px-2" items="center" gap={0.5}>
        {currentBucket && (storageAccessibleBucketList?.length ?? 0) > 1 && (
          <VFlex items="center" gap={0.5}>
            <select
              className="w-[180px] rounded-md border border-gray-300 px-sm py-[7px] text-body-xl focus:border-blue-300"
              value={currentBucket}
              onChange={e => {
                setCurrentBucket(e.target.value);
              }}
            >
              {storageAccessibleBucketList?.map(v => (
                <option value={v} key={v}>
                  {v}
                </option>
              ))}
            </select>
          </VFlex>
        )}
      </VFlex>
      <VFileBrowser
        itemList={itemList}
        emptyRenderer={{
          contentList: () => (
            <VFileBrowserEmptyContentList
              message={t('storage.emptyContentList.gastrohypertonic')}
              onUploadButtonClick={type =>
                onUploadButtonClick(type === 'directory')
              }
            />
          ),
          tree: () => (
            <VFileBrowserEmptyTree message={t('storage.emptyTree.movement')} />
          ),
        }}
        loadingContent={isContentLoading}
        loadingContentRenderer={() => (
          <LoadingIndicator className="relative mx-auto mt-scale/64 h-fit w-fit bg-transparent" />
        )}
        onItemDblClick={event => {
          if (event.target.item.isDir) {
            const uuid = event.target.item.uuid;
            const dir = itemByUuid[uuid];
            const isRoot = dir == null;

            if (isRoot) {
              moveRoot();
            } else {
              setCurrentDirectoryUuid(uuid);
              readDirectory(
                {
                  path: dir.key,
                  childrenOption: { autoAdd: true },
                },
                true,
              );
            }
          }

          setPath({ basePath: '', orgPath: '' });
        }}
        onItemRemove={event => {
          const {
            target: { itemList },
          } = event;

          onRemoveItemList(itemList);
        }}
        onEndOfDirectory={event => {
          const {
            target: {
              item: { uuid },
            },
          } = event;
          const dir = itemByUuid[uuid];
          const startAfter = dir?.lastStartAfter;

          if (!startAfter) return;

          readDirectory({
            startAfter,
            childrenOption: {
              autoAdd: true,
              appended: true,
            },
          });
        }}
        onItemSelect={event => {
          deselectAll();
          select(event.target.itemList);
        }}
        onItemClick={event => {
          deselectAll();
          if (event.target.src === 'tree') {
            setCurrentDirectoryUuid(event.target.item.uuid);

            readDirectory(
              {
                path: event.target.item.key,
                childrenOption: {
                  autoAdd: true,
                },
              },
              true,
            );
          }

          if (event.target.item.isDir && event.target.item.name !== '..') {
            const orgPath = event.target.item.name;
            const basePath =
              event.target.item.key?.replace(`${orgPath}/`, '') ?? '';

            setPath({ basePath, orgPath });
          }
        }}
        onOpenDirectory={event => {
          const dir = itemByUuid[event.target.item.uuid!];

          readDirectory({
            path: dir.key,
            childrenOption: {
              autoAdd: true,
            },
          });
        }}
      />
      <VFlex
        className="w-fit place-self-end"
        justify="end"
        items="center"
        gap={1}
      >
        <div className="flex-1">
          {`${path.basePath}/${path.orgPath}`.replace(/\/+/gi, '/')}
        </div>
        <VButton
          outlined
          size="md"
          variant="secondary"
          onClick={() => {
            setPath({ basePath: '', orgPath: '' });
            onSelect(path);
          }}
        >
          Cancel
        </VButton>
        <VButton
          disabled={!path.orgPath}
          size="md"
          onClick={() => {
            onSelect(path);
          }}
        >
          Select
        </VButton>
      </VFlex>
    </VFlex>
  );
};

export default PathSelector;
